/**
 * useSidebarListAnimations — attach `autoAnimate` to project- and thread-list
 * DOM nodes, idempotently.
 *
 * Self-contained: no React state, no dependencies on other sidebar slices.
 * Each `attach*` callback is safe to pass as a React ref; it will only register
 * the animation the first time a given node is attached (tracked via WeakSet).
 *
 * Extracted from `useSidebarOrchestration` to keep the orchestration hook
 * composition-focused.
 */

import { useCallback, useRef } from "react";

import { autoAnimate } from "@formkit/auto-animate";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * The project tree's reflow. 180ms is Forge's own value, and the project list
 * has no t3code counterpart to match — upstream's Projects layout is a
 * different shape entirely.
 */
const SIDEBAR_LIST_ANIMATION_OPTIONS = {
  duration: 180,
  easing: "ease-out",
} as const;

/**
 * The thread list's reflow, at t3code's 150ms.
 *
 * Deliberately 30ms faster than the project list above. A thread list reorders
 * on every status change — several times a minute with agents running — so the
 * animation is something you watch constantly rather than occasionally, and
 * upstream tunes it shorter for exactly that reason. Sharing one constant
 * between the two lists is what let the thread list drift onto the project
 * list's timing.
 */
const THREAD_LIST_ANIMATION_OPTIONS = {
  duration: 150,
  easing: "ease-out",
} as const;

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export interface SidebarListAnimationsResult {
  readonly attachProjectListAutoAnimateRef: (node: HTMLElement | null) => void;
  readonly attachThreadListAutoAnimateRef: (node: HTMLElement | null) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSidebarListAnimations(): SidebarListAnimationsResult {
  const animatedProjectListsRef = useRef(new WeakSet<HTMLElement>());
  const attachProjectListAutoAnimateRef = useCallback((node: HTMLElement | null) => {
    if (!node || animatedProjectListsRef.current.has(node)) return;
    autoAnimate(node, SIDEBAR_LIST_ANIMATION_OPTIONS);
    animatedProjectListsRef.current.add(node);
  }, []);

  const animatedThreadListsRef = useRef(new WeakSet<HTMLElement>());
  const attachThreadListAutoAnimateRef = useCallback((node: HTMLElement | null) => {
    if (!node || animatedThreadListsRef.current.has(node)) return;
    autoAnimate(node, THREAD_LIST_ANIMATION_OPTIONS);
    animatedThreadListsRef.current.add(node);
  }, []);

  return {
    attachProjectListAutoAnimateRef,
    attachThreadListAutoAnimateRef,
  };
}
